/**
 * Infinite scroll of RingilDataTable v3 (a tab's alternative to paging): rows load in chunks as the
 * user nears the bottom, up to a cap. The backend still pages; these rules decide which page to ask
 * for next, how to add it to what is loaded, and how to refresh without losing the user's place.
 */

export interface InfiniteScrollConfig {
  /** Rows per request. */
  chunkSize: number;
  /** The most rows the table loads; past it the user is asked to narrow the filters. */
  cap: number;
}

export const DEFAULT_INFINITE_SCROLL: InfiniteScrollConfig = { chunkSize: 50, cap: 500 };

/** How a tab shows rows beyond the first screen. `undefined` on a tab = pages. */
export type PagingMode = 'pages' | 'scroll';

/** The table's setting (`true` = the defaults) as a config, or `undefined` when the table doesn't offer scrolling. */
export const resolveInfiniteScroll = (setting: boolean | Partial<InfiniteScrollConfig> | undefined): InfiniteScrollConfig | undefined => {
  if (!setting) return undefined;
  const config = { ...DEFAULT_INFINITE_SCROLL, ...(setting === true ? {} : setting) };
  // The cap is a whole number of chunks, so the last request never asks past it.
  return { chunkSize: config.chunkSize, cap: Math.max(config.chunkSize, Math.floor(config.cap / config.chunkSize) * config.chunkSize) };
};

/** Whether another chunk can be asked for: the result has more, and the cap is not reached. */
export const canLoadMore = (args: { chunksLoaded: number; total: number; config: InfiniteScrollConfig }): boolean => {
  const asked = args.chunksLoaded * args.config.chunkSize;
  return asked < args.total && asked < args.config.cap;
};

/** The cap stopped the list short of the result: the footer asks the user to narrow the filters. */
export const isCapped = (args: { loaded: number; total: number; config: InfiniteScrollConfig }): boolean =>
  args.loaded >= args.config.cap && args.total > args.config.cap;

/**
 * Adds a chunk to the loaded rows. The backend pages by position, so a shipment added or moved
 * since the last chunk shifts the next one and can repeat a row already shown; a repeat is dropped.
 */
export const appendChunk = <T>(loaded: T[], chunk: T[], rowId: (row: T) => unknown, cap: number): T[] => {
  const seen = new Set(loaded.map(rowId));
  return [...loaded, ...chunk.filter(row => !seen.has(rowId(row)))].slice(0, cap);
};

/**
 * One request that brings back everything loaded so far, fresh — after a change in the detail, or
 * Reload — so the list is refreshed where the user is instead of starting over.
 */
export const refreshRequest = (chunksLoaded: number, config: InfiniteScrollConfig): { pageNumber: number; pageSize: number } => ({
  pageNumber: 1,
  pageSize: Math.min(config.cap, Math.max(1, chunksLoaded) * config.chunkSize),
});
